import { useCallback, useEffect, useMemo } from 'react';
import { message } from 'antd';
import {
    PROJECT_PAGE_SIZE,
    LAST_ACTIVE_PROJECT_STORAGE_KEY,
    LAST_ACTIVE_ORG_KEY_STORAGE_KEY,
    SKIP_AUTO_SELECT_QUERY_KEY,
    CREATE_PROJECT_QUERY_KEY,
    TARGET_PROJECT_ID_QUERY_KEY,
    TARGET_PROJECT_NAME_QUERY_KEY,
    TARGET_EXECUTION_ID_QUERY_KEY,
} from '../constants/canvas';
import {
    createFlowProject,
    deleteFlowProject,
    duplicateFlowProject,
    getFlowProjectDetail,
    getFlowProjectPage,
    pinFlowProject,
    saveFlowProject,
    sortFlowProject,
} from '../execution';
import {
    getErrorMessage,
    getVersionNumber,
} from '../utils/common';
import {
    mergeProjectCollections,
    mergeProjectRecord,
    reorderProjectCollections,
} from '../utils/project';
import { buildActiveGroupExecutionMetaMap } from '../utils/execution';

/**
 * 画布项目管理（列表加载、定位、CRUD、引导参数处理）
 */
export const useCanvasProject = ({
    location,
    navigate,
    projects,
    setProjects,
    activeId,
    setActiveId,
    member,
    globalUser,
    globalOrgInfo,
    isPmView,
    forcePersonalMember,
    skipAutoSelect,
    createProjectRequested,
    targetProjectId,
    targetProjectName,
    targetExecutionId,
    nodes,
    edges,
    setSidebarCollapsed,
    setProjectListLoading,
    projectListHasMore,
    setProjectListHasMore,
    resetCanvasState,
    restoreProjectCanvas,
    pollNodeExecution,
    pollGroupExecution,
    focusNodeByExecutionId,
    stopAllExecutionPolling,
    syncProjectVersion,
    getCurrentProjectVersion,
    projectVersionMapRef,
    currentProjectVersionRef,
    activeProjectRef,
    projectListLoadingRef,
    projectPageIndexRef,
    projectKeywordRef,
    projectRequestIdRef,
    projectDetailRequestIdRef,
    lastActiveProjectIdRef,
    lastActiveProjectPageIndexRef,
    hasHydratedLastActiveProjectRef,
    createProjectHandledRef,
    skipProjectDetailLoadRef,
    targetProjectHandledRef,
    targetProjectLocateTokenRef,
    targetExecutionHandledRef,
}) => {
    const resolvedProjectMember = useMemo(() => {
        if (isPmView) {
            return undefined;
        }
        if (forcePersonalMember) {
            return globalUser?.id || '';
        }
        if (member === 'team') {
            return null;
        }
        return member || globalUser?.id || '';
    }, [forcePersonalMember, globalUser?.id, isPmView, member]);
    const projectMemberReady = useMemo(() => {
        if (isPmView || member === 'team') {
            return true;
        }
        return !!resolvedProjectMember;
    }, [isPmView, member, resolvedProjectMember]);

    // 加载项目列表，支持分页和关键词搜索
    const loadProjectList = useCallback(async ({
        pageIndex = 1,
        append = false,
        keyword = '',
        autoSelect = true,
    } = {}) => {
        if (!projectMemberReady) return null;
        if (resolvedProjectMember === null && member !== 'team' && !forcePersonalMember) return null;

        const requestId = projectRequestIdRef.current + 1;
        projectRequestIdRef.current = requestId;
        projectListLoadingRef.current = true;
        setProjectListLoading(true);

        try {
            const pageData = await getFlowProjectPage({
                keyword,
                pageIndex,
                pageSize: PROJECT_PAGE_SIZE,
                member: resolvedProjectMember,
            });

            if (projectRequestIdRef.current !== requestId) return;

            projectPageIndexRef.current = pageIndex;
            projectKeywordRef.current = keyword;
            setProjects((prev) => mergeProjectCollections(prev, pageData.items, append));
            setProjectListHasMore(pageData.items.length >= PROJECT_PAGE_SIZE && pageData.pageIndex < pageData.totalPages);
            const currentActiveProject = pageData.items.find((item) => item.id === activeProjectRef.current?.id);
            if (currentActiveProject) {
                currentProjectVersionRef.current = Math.max(
                    getVersionNumber(currentProjectVersionRef.current),
                    getVersionNumber(currentActiveProject.version),
                );
            }
            if (!append && autoSelect) {
                setActiveId((prev) => (
                    prev && pageData.items.some((item) => item.id === prev)
                        ? prev
                        : (
                            !prev && !keyword && pageIndex === 1 && !skipAutoSelect
                                ? (
                                    pageData.items.find((item) => item.id === lastActiveProjectIdRef.current)?.id
                                    || pageData.items[0]?.id
                                    || ''
                                )
                                : prev
                        )
                ));
            }

            return pageData;
        } catch (error) {
            console.error(error);
            return null;
        } finally {
            if (projectRequestIdRef.current === requestId) {
                projectListLoadingRef.current = false;
                setProjectListLoading(false);
            }
        }
    }, [activeProjectRef, currentProjectVersionRef, forcePersonalMember, lastActiveProjectIdRef, member, projectKeywordRef, projectListLoadingRef, projectPageIndexRef, projectMemberReady, projectRequestIdRef, resolvedProjectMember, skipAutoSelect, setActiveId, setProjectListHasMore, setProjectListLoading, setProjects]);

    const locateProjectByPaging = useCallback(async (projectId, shouldAbort = () => false) => {
        if (!projectId) return false;

        let aggregatedItems = [];
        let firstProjectId = '';
        for (let pageIndex = 1; ; pageIndex += 1) {
            if (shouldAbort()) return null;

            const pageData = await loadProjectList({
                pageIndex,
                append: pageIndex > 1,
                keyword: '',
                autoSelect: false,
            });

            if (!pageData) return null;
            if (shouldAbort()) return null;

            aggregatedItems = mergeProjectCollections(aggregatedItems, pageData.items, pageIndex > 1);
            firstProjectId = firstProjectId || pageData.items[0]?.id || '';

            if (aggregatedItems.some((item) => item.id === projectId)) {
                if (shouldAbort()) return null;
                setActiveId(projectId);
                return true;
            }

            if (pageData.items.length < PROJECT_PAGE_SIZE || pageData.pageIndex >= pageData.totalPages) {
                break;
            }
        }

        if (shouldAbort()) return null;
        setActiveId((prev) => prev || firstProjectId || '');
        return false;
    }, [loadProjectList, setActiveId]);

    useEffect(() => {
        const restoreLastActiveProject = async () => {
            if (targetProjectId) {
                return;
            }
            if (activeId) {
                return;
            }
            const rememberedProjectId = lastActiveProjectIdRef.current;
            const preservedKeyword = projectKeywordRef.current.trim();

            if (preservedKeyword) {
                return;
            }

            if (skipAutoSelect || !rememberedProjectId) {
                await loadProjectList({ pageIndex: 1, append: false, keyword: '' });
                return;
            }

            await locateProjectByPaging(rememberedProjectId);
        };

        restoreLastActiveProject();
    }, [activeId, lastActiveProjectIdRef, loadProjectList, locateProjectByPaging, projectKeywordRef, skipAutoSelect, targetProjectId]);

    useEffect(() => {
        if (!targetProjectId) {
            targetProjectHandledRef.current = '';
            return;
        }
        if (targetProjectHandledRef.current === targetProjectId) return;

        const locateProject = async () => {
            const locateToken = targetProjectLocateTokenRef.current + 1;
            targetProjectLocateTokenRef.current = locateToken;
            try {
                const located = await locateProjectByPaging(
                    targetProjectId,
                    () => targetProjectLocateTokenRef.current !== locateToken,
                );
                if (located === null) {
                    return;
                }
                if (!located) {
                    setActiveId(targetProjectId);
                }
                targetProjectHandledRef.current = targetProjectId;
            } catch (error) {
                console.error(error);
            }
        };

        locateProject();
    }, [locateProjectByPaging, setActiveId, targetProjectHandledRef, targetProjectId, targetProjectLocateTokenRef]);

    useEffect(() => {
        if (typeof window === 'undefined') return;

        if (!hasHydratedLastActiveProjectRef.current) {
            hasHydratedLastActiveProjectRef.current = true;
            if (!activeId) return;
        }

        const activeProjectIndex = projects.findIndex((item) => item.id === activeId);
        const nextPageIndex = activeProjectIndex >= 0
            ? Math.floor(activeProjectIndex / PROJECT_PAGE_SIZE) + 1
            : Math.max(1, Number(lastActiveProjectPageIndexRef.current) || 1);

        lastActiveProjectIdRef.current = activeId || '';
        lastActiveProjectPageIndexRef.current = nextPageIndex;

        if (activeId) {
            window.localStorage.setItem(LAST_ACTIVE_PROJECT_STORAGE_KEY, JSON.stringify({
                projectId: activeId,
                pageIndex: nextPageIndex,
            }));
            return;
        }

        window.localStorage.removeItem(LAST_ACTIVE_PROJECT_STORAGE_KEY);
    }, [activeId, hasHydratedLastActiveProjectRef, lastActiveProjectIdRef, lastActiveProjectPageIndexRef, projects]);

    useEffect(() => {
        if (!activeId) {
            resetCanvasState();
            return undefined;
        }

        if (skipProjectDetailLoadRef.current === activeId) {
            skipProjectDetailLoadRef.current = '';
            return undefined;
        }

        const requestId = Date.now();
        projectDetailRequestIdRef.current = requestId;
        resetCanvasState();

        const loadProjectDetail = async () => {
            try {
                const projectDetail = await getFlowProjectDetail(activeId);
                if (projectDetailRequestIdRef.current !== requestId) return;

                setProjects((prev) => prev.map((project) => (
                    project.id === activeId
                        ? mergeProjectRecord(project, {
                            ...project,
                            name: projectDetail.name || project.name,
                            cover: projectDetail.cover,
                            studioId: projectDetail.studioId,
                            version: projectDetail.version,
                        })
                        : project
                )));
                currentProjectVersionRef.current = Math.max(
                    getVersionNumber(currentProjectVersionRef.current),
                    getVersionNumber(projectDetail.version),
                );
                syncProjectVersion(activeId, projectDetail.version);
                restoreProjectCanvas(projectDetail);
                (projectDetail.nodes || []).forEach((node) => {
                    const latestExecution = node?.latestExecution;
                    const renderNodeData = node?.renderData?.data || {};
                    const latestStatus = String(
                        latestExecution?.status
                        || renderNodeData?.executionStatus
                        || '',
                    ).toLowerCase();
                    const activeExecutionId = String(
                        latestExecution?.executionId
                        || renderNodeData?.activeExecutionId
                        || '',
                    );
                    if (
                        !node?.parentId
                        && (latestStatus === 'processing' || latestStatus === 'manual')
                        && activeExecutionId
                        && node?.id
                    ) {
                        pollNodeExecution(String(node.id), activeExecutionId);
                    }
                });
                const restoredNodes = Array.isArray(projectDetail?.nodes) ? projectDetail.nodes : [];
                const activeGroupExecutionMetaMap = buildActiveGroupExecutionMetaMap(restoredNodes);

                activeGroupExecutionMetaMap.forEach((meta) => {
                    if (!meta?.executionId || !meta?.groupFunctionNodeIds?.length) return;

                    pollGroupExecution(
                        String(meta.groupNodeId),
                        String(meta.executionId),
                        meta.groupFunctionNodeIds,
                        meta.groupChildNodeIds,
                    );
                });
            } catch (error) {
                console.error(error);
                message.error(getErrorMessage(error, '加载项目详情失败'));
            }
        };

        loadProjectDetail();

        return () => {
            if (projectDetailRequestIdRef.current === requestId) {
                projectDetailRequestIdRef.current = 0;
            }
        };
    }, [activeId, currentProjectVersionRef, pollGroupExecution, pollNodeExecution, projectDetailRequestIdRef, resetCanvasState, restoreProjectCanvas, skipProjectDetailLoadRef, syncProjectVersion, setProjects]);

    useEffect(() => {
        if (!targetExecutionId) {
            targetExecutionHandledRef.current = '';
            return;
        }

        if (!targetProjectId || activeId !== targetProjectId) return;

        const handledKey = `${targetProjectId}:${targetExecutionId}`;
        if (targetExecutionHandledRef.current === handledKey) return;
        if (!nodes.length) return;

        if (focusNodeByExecutionId(targetExecutionId)) {
            targetExecutionHandledRef.current = handledKey;
        }
    }, [activeId, focusNodeByExecutionId, nodes.length, targetExecutionHandledRef, targetExecutionId, targetProjectId]);

    // 根据关键词刷新项目列表
    const handleProjectKeywordChange = useCallback((keyword) => {
        const normalizedKeyword = keyword.trim();
        projectKeywordRef.current = normalizedKeyword;

        loadProjectList({
            pageIndex: 1,
            append: false,
            keyword: normalizedKeyword,
        });
    }, [loadProjectList, projectKeywordRef]);

    // 加载更多项目列表数据
    const handleLoadMoreProjects = useCallback(() => {
        if (!projectListHasMore || projectListLoadingRef.current) return;

        loadProjectList({
            pageIndex: projectPageIndexRef.current + 1,
            append: true,
            keyword: projectKeywordRef.current,
        });
        // projectListHasMore 为外部传入的 state，需保留以刷新闭包
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loadProjectList, projectKeywordRef, projectListHasMore, projectListLoadingRef, projectPageIndexRef]);

    // 切换当前项目，并清理引导参数
    const handleSelectProject = useCallback((projectId) => {
        targetProjectLocateTokenRef.current += 1;
        stopAllExecutionPolling();
        lastActiveProjectIdRef.current = projectId || '';
        if (projectId && typeof window !== 'undefined') {
            const activeProjectIndex = projects.findIndex((item) => item.id === projectId);
            const pageIndex = activeProjectIndex >= 0
                ? Math.floor(activeProjectIndex / PROJECT_PAGE_SIZE) + 1
                : Math.max(1, Number(lastActiveProjectPageIndexRef.current) || 1);
            lastActiveProjectPageIndexRef.current = pageIndex;
            window.localStorage.setItem(LAST_ACTIVE_PROJECT_STORAGE_KEY, JSON.stringify({
                projectId,
                pageIndex,
            }));
        }
        setActiveId(projectId);
        const searchParams = new URLSearchParams(location.search);
        let changed = false;

        [
            SKIP_AUTO_SELECT_QUERY_KEY,
            CREATE_PROJECT_QUERY_KEY,
            TARGET_PROJECT_ID_QUERY_KEY,
            TARGET_PROJECT_NAME_QUERY_KEY,
            TARGET_EXECUTION_ID_QUERY_KEY,
        ].forEach((key) => {
            if (searchParams.has(key)) {
                searchParams.delete(key);
                changed = true;
            }
        });

        if (changed) {
            navigate({
                pathname: location.pathname,
                search: searchParams.toString() ? `?${searchParams.toString()}` : '',
            }, { replace: true });
        }
    }, [lastActiveProjectIdRef, lastActiveProjectPageIndexRef, location.pathname, location.search, navigate, projects, setActiveId, stopAllExecutionPolling, targetProjectLocateTokenRef]);

    // 清理 URL 中的项目引导相关参数
    const clearGuideQueryParams = useCallback(() => {
        const searchParams = new URLSearchParams(location.search);
        let changed = false;

        [
            SKIP_AUTO_SELECT_QUERY_KEY,
            CREATE_PROJECT_QUERY_KEY,
            TARGET_PROJECT_ID_QUERY_KEY,
            TARGET_PROJECT_NAME_QUERY_KEY,
            TARGET_EXECUTION_ID_QUERY_KEY,
        ].forEach((key) => {
            if (searchParams.has(key)) {
                searchParams.delete(key);
                changed = true;
            }
        });

        if (changed) {
            navigate({
                pathname: location.pathname,
                search: searchParams.toString() ? `?${searchParams.toString()}` : '',
            }, { replace: true });
        }
    }, [location.pathname, location.search, navigate]);

    // 新建空白项目并切换到该项目
    const handleCreate = useCallback(async () => {
        try {
            const nextProject = await createFlowProject();

            setProjects((prev) => [nextProject, ...prev.filter((item) => item.id !== nextProject.id)]);
            skipProjectDetailLoadRef.current = nextProject.id;
            setActiveId(nextProject.id);
            resetCanvasState();
            setSidebarCollapsed(false);
            clearGuideQueryParams();
            // 新建完成后主动刷新一次列表，确保侧边栏数据与服务端同步
            loadProjectList({ pageIndex: 1, append: false, keyword: projectKeywordRef.current || '' });
        } catch (error) {
            console.error(error);
            message.error(getErrorMessage(error, '创建项目失败'));
        }
    }, [clearGuideQueryParams, loadProjectList, projectKeywordRef, resetCanvasState, setActiveId, setProjects, setSidebarCollapsed, skipProjectDetailLoadRef]);

    useEffect(() => {
        if (isPmView) return;
        if (typeof window === 'undefined') return;
        if (createProjectRequested) return;

        const currentOrgKey = globalOrgInfo?.isPersonal
            ? 'personal'
            : `org:${globalOrgInfo?.current?.id || ''}`;
        if (!currentOrgKey || currentOrgKey === 'org:') return;

        const previousOrgKey = window.sessionStorage.getItem(LAST_ACTIVE_ORG_KEY_STORAGE_KEY) || '';
        window.sessionStorage.setItem(LAST_ACTIVE_ORG_KEY_STORAGE_KEY, currentOrgKey);

        if (!previousOrgKey || previousOrgKey === currentOrgKey) {
            return;
        }

        clearGuideQueryParams();
        handleCreate();
    }, [clearGuideQueryParams, createProjectRequested, globalOrgInfo, globalOrgInfo?.current?.id, globalOrgInfo?.isPersonal, handleCreate, isPmView]);

    useEffect(() => {
        if (!createProjectRequested || isPmView) {
            createProjectHandledRef.current = false;
            return;
        }
        if (createProjectHandledRef.current) return;

        createProjectHandledRef.current = true;
        handleCreate().finally(() => {
            clearGuideQueryParams();
        });
    }, [clearGuideQueryParams, createProjectHandledRef, createProjectRequested, handleCreate, isPmView]);

    // 修改当前项目名称
    const handleNameChange = useCallback(async (newName) => {
        const currentProject = projects.find((project) => project.id === activeId);
        if (!currentProject) return false;
        const normalizedName = String(newName || '').trim().slice(0, 20);
        if (!normalizedName) return false;

        try {
            const nextProject = await saveFlowProject({
                id: currentProject.id,
                projectName: normalizedName,
                cover: currentProject.cover,
                successMessage: '重命名成功',
                studioId: currentProject.studioId,
            });

            setProjects((prev) => prev.map((project) => (
                project.id === currentProject.id ? { ...project, ...nextProject } : project
            )));
            return true;
        } catch (error) {
            console.error(error);
            return false;
        }
    }, [activeId, projects, setProjects]);

    // 修改指定项目名称
    const handleRenameProject = useCallback(async (projectId, newName) => {
        const currentProject = projects.find((project) => project.id === projectId);
        if (!currentProject) return false;

        try {
            const nextProject = await saveFlowProject({
                id: currentProject.id,
                projectName: newName,
                cover: currentProject.cover,
                successMessage: '重命名成功',
                studioId: currentProject.studioId,
            });

            setProjects((prev) => prev.map((project) => (
                project.id === projectId ? { ...project, ...nextProject } : project
            )));
            return true;
        } catch (error) {
            console.error(error);
            return false;
        }
    }, [projects, setProjects]);

    const handleDuplicateProject = useCallback(async (projectId) => {
        const currentProject = projects.find((project) => project.id === projectId);
        if (!currentProject) return false;

        try {
            const nextProject = mergeProjectRecord(
                {},
                await duplicateFlowProject({
                    projectId,
                    projectName: currentProject.name || '未命名项目',
                    cover: currentProject.cover || '',
                    successMessage: '副本已创建',
                }),
            );

            setProjects((prev) => [nextProject, ...prev.filter((project) => project.id !== nextProject.id)]);
            setActiveId(nextProject.id);
            setSidebarCollapsed(false);
            await loadProjectList({
                pageIndex: 1,
                append: false,
                keyword: projectKeywordRef.current || '',
            });
            setActiveId(nextProject.id);
            return true;
        } catch (error) {
            console.error(error);
            message.error(getErrorMessage(error, '创建副本失败'));
            return false;
        }
    }, [loadProjectList, projectKeywordRef, projects, setActiveId, setProjects, setSidebarCollapsed]);

    // 删除指定项目，并处理当前激活项目回退
    const handlePinProject = useCallback(async (projectId) => {
        if (!projectId) return false;

        try {
            await pinFlowProject(projectId);
            await loadProjectList({
                pageIndex: 1,
                append: false,
                keyword: projectKeywordRef.current || '',
            });
            return true;
        } catch (error) {
            console.error(error);
            return false;
        }
    }, [loadProjectList, projectKeywordRef]);

    const handleSortProject = useCallback(async ({
        id,
        targetProjectId,
        position,
    } = {}) => {
        if (!id || !targetProjectId || !position || id === targetProjectId) return false;

        const projectIndexMap = new Map(projects.map((item, index) => [item.id, index]));
        const oldIdx = projectIndexMap.get(id);
        const targetIdx = projectIndexMap.get(targetProjectId);

        if (oldIdx === undefined || targetIdx === undefined) return false;

        const newIdx = position === 'before'
            ? (oldIdx < targetIdx ? targetIdx - 1 : targetIdx)
            : (oldIdx < targetIdx ? targetIdx : targetIdx + 1);

        if (oldIdx === newIdx) return false;

        const previousProjects = projects;
        const reorderedProjects = reorderProjectCollections(previousProjects, oldIdx, newIdx);
        if (reorderedProjects === previousProjects) return false;

        const afterProjectId = reorderedProjects[newIdx - 1]?.id || '';
        setProjects(reorderedProjects);

        try {
            await sortFlowProject({
                id,
                afterProjectId,
            });
            return true;
        } catch (error) {
            console.error(error);
            setProjects(previousProjects);
            return false;
        }
    }, [projects, setProjects]);

    const handleDeleteProject = useCallback(async (projectId) => {
        try {
            await deleteFlowProject(projectId);

            setProjects((prev) => {
                const nextProjects = prev.filter((project) => project.id !== projectId);

                setActiveId((currentActiveId) => {
                    if (currentActiveId !== projectId) return currentActiveId;
                    return nextProjects[0]?.id || '';
                });

                return nextProjects;
            });

            if (activeId === projectId) {
                resetCanvasState();
            }

            return true;
        } catch (error) {
            console.error(error);
            return false;
        }
    }, [activeId, resetCanvasState, setActiveId, setProjects]);

    // 确保当前画布操作有可用项目承载
    const ensureCanvasProject = useCallback(async () => {
        const currentProjectId = activeId || activeProjectRef.current?.id || '';

        if (currentProjectId) {
            return {
                projectId: currentProjectId,
                created: false,
            };
        }

        const nextProject = await createFlowProject();
        setProjects((prev) => [nextProject, ...prev.filter((item) => item.id !== nextProject.id)]);
        skipProjectDetailLoadRef.current = nextProject.id;
        handleSelectProject(nextProject.id);
        await loadProjectList({
            pageIndex: 1,
            append: false,
            keyword: projectKeywordRef.current || '',
        });

        return {
            projectId: nextProject.id,
            created: true,
        };
    }, [activeId, activeProjectRef, handleSelectProject, loadProjectList, projectKeywordRef, setProjects, skipProjectDetailLoadRef]);

    return {
        resolvedProjectMember,
        projectMemberReady,
        loadProjectList,
        locateProjectByPaging,
        handleProjectKeywordChange,
        handleLoadMoreProjects,
        handleSelectProject,
        clearGuideQueryParams,
        handleCreate,
        handleNameChange,
        handleRenameProject,
        handleDuplicateProject,
        handlePinProject,
        handleSortProject,
        handleDeleteProject,
        ensureCanvasProject,
    };
};